<template>
  <div>
    <div class="main-wrapper">
      <sidebar-component />
      <div class="page-wrapper">
        <navbar-component />
        <div class="page-content">
          <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
            <div>
              <h4 class="mb-3 mb-md-0">Information Event Detail</h4>
            </div>
            <div class="d-flex align-items-center flex-wrap text-nowrap">
              <router-link :to="{ name: 'InformationEventsIndex' }" class="btn btn-secondary mb-2 mb-md-0 me-2">
                Back to List
              </router-link>
              <router-link 
                :to="{ name: 'InformationEventsEdit', params: { id: $route.params.id } }" 
                class="btn btn-warning mb-2 mb-md-0"
              >
                Edit Event
              </router-link>
            </div>
          </div>

          <div class="row">
            <div class="col-12 stretch-card">
              <div class="card">
                <div class="card-body">
                  <div v-if="isLoading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                  </div>

                  <div v-else-if="event">
                    <!-- Event Image -->
                    <div v-if="event.image" class="mb-4 text-center">
                      <img 
                        :src="event.image" 
                        :alt="event.name" 
                        class="img-fluid rounded"
                        style="max-height: 400px; object-fit: cover;"
                      />
                    </div>

                    <!-- Event Header -->
                    <div class="d-flex justify-content-between align-items-start mb-4">
                      <div>
                        <h2 class="mb-2">{{ event.name }}</h2>
                        <div class="d-flex align-items-center gap-3">
                          <span 
                            class="badge fs-6" 
                            :class="{
                              'bg-success': event.status === 'ongoing',
                              'bg-info': event.status === 'upcoming',
                              'bg-secondary': event.status === 'past'
                            }"
                          >
                            {{ getStatusText(event.status) }}
                          </span>
                          <span 
                            class="badge fs-6" 
                            :class="event.is_active ? 'bg-success' : 'bg-danger'"
                          >
                            {{ event.is_active ? 'Active' : 'Inactive' }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Event Information -->
                    <div class="row">
                      <div class="col-md-6">
                        <div class="card bg-light">
                          <div class="card-body">
                            <h5 class="card-title">Event Information</h5>
                            <table class="table table-borderless">
                              <tr>
                                <td class="fw-bold">Start Date:</td>
                                <td>{{ event.formatted_start_date }}</td>
                              </tr>
                              <tr>
                                <td class="fw-bold">End Date:</td>
                                <td>{{ event.formatted_end_date }}</td>
                              </tr>
                              <tr>
                                <td class="fw-bold">Duration:</td>
                                <td>{{ event.duration_in_days }} day(s)</td>
                              </tr>
                              <tr v-if="event.link_event">
                                <td class="fw-bold">Event Link:</td>
                                <td>
                                  <a :href="event.link_event" target="_blank" class="text-decoration-none">
                                    {{ event.link_event }}
                                    <i class="link-icon ms-1" data-feather="external-link"></i>
                                  </a>
                                </td>
                              </tr>
                            </table>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div class="card bg-light">
                          <div class="card-body">
                            <h5 class="card-title">System Information</h5>
                            <table class="table table-borderless">
                              <tr>
                                <td class="fw-bold">Created At:</td>
                                <td>{{ formatDateTime(event.created_at) }}</td>
                              </tr>
                              <tr>
                                <td class="fw-bold">Updated At:</td>
                                <td>{{ formatDateTime(event.updated_at) }}</td>
                              </tr>
                              <tr>
                                <td class="fw-bold">Event ID:</td>
                                <td><code>{{ event.id }}</code></td>
                              </tr>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Event Description -->
                    <div v-if="event.description" class="mt-4">
                      <div class="card">
                        <div class="card-body">
                          <h5 class="card-title">Description</h5>
                          <div class="description-content" v-html="formatDescription(event.description)"></div>
                        </div>
                      </div>
                    </div>

                    <!-- Calendar Integration -->
                    <div class="mt-4">
                      <div class="card">
                        <div class="card-body">
                          <h5 class="card-title">Calendar Integration</h5>
                          <p class="text-muted mb-3">Add this event to your calendar</p>
                          <div class="d-flex gap-2 flex-wrap">
                            <button @click="addToGoogleCalendar" class="btn btn-outline-primary">
                              <i class="link-icon me-1" data-feather="calendar"></i>
                              Google Calendar
                            </button>
                            <button @click="addToOutlookCalendar" class="btn btn-outline-info">
                              <i class="link-icon me-1" data-feather="calendar"></i>
                              Outlook Calendar
                            </button>
                            <button @click="downloadICS" class="btn btn-outline-secondary">
                              <i class="link-icon me-1" data-feather="download"></i>
                              Download ICS
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="text-center py-5">
                    <h5 class="text-muted">Event not found</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <footer-component />
      </div>
    </div>
  </div>
</template>

<script>
import sidebarComponent from "@/components/_partials/_sidebar";
import navbarComponent from "@/components/_partials/_navbar";
import footerComponent from "@/components/_partials/_footer";

export default {
  name: "InformationEventsDetail",
  components: {
    "sidebar-component": sidebarComponent,
    "navbar-component": navbarComponent,
    "footer-component": footerComponent,
  },
  data() {
    return {
      isLoading: false,
      event: null
    };
  },
  methods: {
    loadEventDetail() {
      const eventId = this.$onBehind(this.$route.params.id);
      const baseURI = this.$settings.endPoint + "information-events/detail/" + eventId;
      
      this.isLoading = true;
      
      return this.$http
        .get(baseURI, {
          timeout: 10000,
          headers: {
            'AppToken': this.$settings.jwtSecret,
          }
        })
        .then((response) => {
          this.isLoading = false;
          if (response.data.status === 200) {
            this.event = response.data.datas;
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.handleError(error);
        });
    },

    getStatusText(status) {
      switch (status) {
        case 'upcoming':
          return 'Upcoming';
        case 'ongoing':
          return 'Ongoing';
        case 'past':
          return 'Past';
        default:
          return status;
      }
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('id-ID', { 
        weekday: 'long',
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDescription(description) {
      if (!description) return '';
      // Convert line breaks to HTML
      return description.replace(/\n/g, '<br>');
    },

    // Calendar integration methods
    addToGoogleCalendar() {
      if (!this.event) return;
      
      const startDate = new Date(this.event.start_date);
      const endDate = new Date(this.event.end_date);
      
      const googleUrl = new URL('https://calendar.google.com/calendar/render');
      googleUrl.searchParams.set('action', 'TEMPLATE');
      googleUrl.searchParams.set('text', this.event.name);
      googleUrl.searchParams.set('dates', 
        `${this.formatDateForCalendar(startDate)}/${this.formatDateForCalendar(endDate)}`
      );
      googleUrl.searchParams.set('details', this.event.description || '');
      if (this.event.link_event) {
        googleUrl.searchParams.set('location', this.event.link_event);
      }
      
      window.open(googleUrl.toString(), '_blank');
    },

    addToOutlookCalendar() {
      if (!this.event) return;
      
      const startDate = new Date(this.event.start_date);
      const endDate = new Date(this.event.end_date);
      
      const outlookUrl = new URL('https://outlook.live.com/calendar/0/deeplink/compose');
      outlookUrl.searchParams.set('subject', this.event.name);
      outlookUrl.searchParams.set('startdt', startDate.toISOString());
      outlookUrl.searchParams.set('enddt', endDate.toISOString());
      outlookUrl.searchParams.set('body', this.event.description || '');
      if (this.event.link_event) {
        outlookUrl.searchParams.set('location', this.event.link_event);
      }
      
      window.open(outlookUrl.toString(), '_blank');
    },

    downloadICS() {
      if (!this.event) return;
      
      const startDate = new Date(this.event.start_date);
      const endDate = new Date(this.event.end_date);
      
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//BERT//Information Events//EN',
        'BEGIN:VEVENT',
        `UID:${this.event.id}@bert-events.com`,
        `DTSTART:${this.formatDateForICS(startDate)}`,
        `DTEND:${this.formatDateForICS(endDate)}`,
        `SUMMARY:${this.event.name}`,
        `DESCRIPTION:${(this.event.description || '').replace(/\n/g, '\\n')}`,
        this.event.link_event ? `URL:${this.event.link_event}` : '',
        `DTSTAMP:${this.formatDateForICS(new Date())}`,
        'END:VEVENT',
        'END:VCALENDAR'
      ].filter(line => line !== '').join('\r\n');
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
      link.click();
    },

    formatDateForCalendar(date) {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    },

    formatDateForICS(date) {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    },

    handleError(error) {
      if (error.response) {
        if (error.response.status === 404) {
          this.$swal('Error', 'Information event not found', 'error');
          this.$router.push({ name: 'InformationEventsIndex' });
        } else if (error.response.status === 500) {
          this.$swal('Error', 'Internal server error', 'error');
        } else if (error.response.status === 401) {
          this.$router.push("/authorized-error");
        } else {
          this.$swal('Error', 'An error occurred', 'error');
        }
      } else {
        this.$swal('Error', 'Network error occurred', 'error');
      }
    }
  },

  mounted() {
    this.loadEventDetail();
  }
};
</script>

<style scoped>
.card {
  box-shadow: 0 0 1rem 0 rgba(136, 152, 170, 0.15);
}

.description-content {
  line-height: 1.6;
  color: #495057;
}

.badge {
  font-size: 0.875rem;
}

.table td {
  padding: 0.5rem 0;
}

.btn {
  margin-right: 0.5rem;
}

.btn:last-child {
  margin-right: 0;
}

@media (max-width: 768px) {
  .d-flex.gap-2 {
    flex-direction: column;
  }
  
  .btn {
    margin-right: 0;
    margin-bottom: 0.5rem;
  }
  
  .btn:last-child {
    margin-bottom: 0;
  }
}
</style>